import React, { useContext, useMemo, useState } from 'react';
import { FormContainer, useForm } from 'react-hook-form-mui';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

import { activateInvitation } from '../../apis/users';
import { SessionContext, supabase, useUpdateProfile } from '../../auth';
import { ErrorMessage, StyledTypography } from '../../components/Auth/Styled';
import PasswordField from '../../components/Common/PasswordField';
import paths from '../../routes/paths';

interface SetPasswordForm {
  password: string;
  repeatPassword: string;
}

export default function SetPassword() {
  const passwordMessage = 'Minimum 8 characters, including 1 lower case letter, 1 upper case letter and 1 number';
  const formContext = useForm<SetPasswordForm>({
    resolver: yupResolver(yup.object().shape({
      password: yup.string().required().min(8, passwordMessage)
        .matches(/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/, passwordMessage),
      repeatPassword: yup.string().required('Repeat password is a required field')
        .oneOf([yup.ref('password'), ''], 'Passwords must match'),
    })),
  });
  const { handleSubmit } = formContext;

  const { session, profile } = useContext(SessionContext);
  const updateProfile = useUpdateProfile();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const location = useLocation();
  const inviteError = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const desc = params.get('error_description') || params.get('error') || '';
    return desc ? decodeURIComponent(desc.replace(/\+/g, ' ')) : null;
  }, [location.search]);

  if (profile?.status === 'active' && profile.organizations.length > 0) {
    return <Navigate to={paths.dashboard.root} replace />;
  }

  if (!session) {
    return (
      <>
        <ErrorMessage>
          {inviteError ?? 'This invitation link is invalid or has expired.'}
        </ErrorMessage>
        <Button
          variant="contained"
          component={Link}
          to={paths.auth.login}
          sx={{ alignSelf: 'center', marginTop: '20px' }}
        >
          Go to login page
        </Button>
      </>
    );
  }

  const { user: { email } } = session;

  const onSubmit = async ({ password }: SetPasswordForm) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setSubmitError('Your password could not be set. Please try again.');
        return;
      }

      await activateInvitation();
      await updateProfile();
      setCompleted(true);
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (completed) {
    return <Navigate to={paths.dashboard.root} replace />;
  }

  return (
    <FormContainer
      formContext={formContext}
      handleSubmit={handleSubmit(onSubmit)}
      FormProps={{ style: { display: 'flex', flexDirection: 'column' } }}
    >
      <StyledTypography sx={{ margin: '0 0 12px 0' }}>
        Setting up account for {email}
      </StyledTypography>
      {submitError && (
        <ErrorMessage>
          {submitError}
        </ErrorMessage>
      )}
      <PasswordField label="Password" name="password" sx={{ marginBottom: '12px' }} />
      <PasswordField label="Repeat password" name="repeatPassword" sx={{ marginBottom: '12px' }} />
      <Button
        variant="contained"
        sx={{ alignSelf: 'flex-end' }}
        type="submit"
        disabled={submitting}
        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {submitting ? 'Setting…' : 'Set password'}
      </Button>
    </FormContainer>
  );
}

